import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactDatePicker from "react-datepicker";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FilePond } from "react-filepond";
import organizationData from "../../../utils/organizationData.json";
 import { validationSchema } from "./validationUser";
import { UserSalesSubmitData } from "../../../types/sales/Sales";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { UserListTableData } from "../../../data/users/UsersList";
import { useRoles } from "../../../context/RolesContext";
import Breadcrumbs from "../../../common-elements/breadcrumbs/Breadcrumbs";

const generatePassword = () => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

interface FlagOption {
  name: string;
  value: string;
  flag?: string;
}

const AddUserPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role, id } = useParams<{ role?: string; id?: string }>();
  const { roles, isLoading } = useRoles();
  const [userData, setUserData] = useState<any[]>([]);
  const [password, setPassword] = useState("");
  const [files, setFiles] = useState<any[]>([]);
  const [copyTooltip, setCopyTooltip] = useState("");
  const [passwordOption, setPasswordOption] = useState("setOwn");
  const passwordInputRef = useRef<any>(null);

  
  useEffect(() => {
    if (!isLoading && role && !roles.includes(role.toLowerCase())) {
      navigate(`${process.env.PUBLIC_URL}/error`, { replace: true });
    }
  }, [role, roles, isLoading, navigate]);

  const isEditMode = !!id;

  if (isLoading) {
    return <div>Loading roles...</div>;
  }

  

  const getRoleTranslation = (role: string) => {
    const formattedRole = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    const translated = t(formattedRole);
    return translated === formattedRole ? formattedRole : translated;
  };

  const breadcrumbLinks = [
    {
      label: t("Users"),
      link: role ? `${process.env.PUBLIC_URL}/users/${role.toLowerCase()}` : `${process.env.PUBLIC_URL}/users`,
    },
    {
      label: role ? getRoleTranslation(role) : t("Users"),
      link: role ? `${process.env.PUBLIC_URL}/users/${role.toLowerCase()}` : `${process.env.PUBLIC_URL}/users`,
    },
  ];

  const saveButtonText = isEditMode ? t("Modify") : t("Save");

  const initialValues: any = isEditMode
    ? {
        ...UserListTableData.find((user) => user.id.toString() === id),
        firstName:
          UserListTableData.find((user) => user.id.toString() === id)?.name.split(" ")[0] || "",
        lastName:
          UserListTableData.find((user) => user.id.toString() === id)?.name.split(" ")[1] || "",
        email: UserListTableData.find((user) => user.id.toString() === id)?.email || "",
        dob: UserListTableData.find((user) => user.id.toString() === id)?.birthDate
          ? new Date(UserListTableData.find((user) => user.id.toString() === id)?.birthDate!)
          : null,
        nationality: ["United Kingdom"],
        mobileCountryCode: "+44",
        mobile: UserListTableData.find((user) => user.id.toString() === id)?.phonenumber || "",
        gender: UserListTableData.find((user) => user.id.toString() === id)?.gender || "",
        image: UserListTableData.find((user) => user.id.toString() === id)?.image || "",
        emailVerification: false,
        mobileVerification: false,
        passwordResetRequired: false,
        theme: "",
        language: "en",
        password: "",
        passwordOption: "setOwn",
      }
    : {
        firstName: "",
        lastName: "",
        email: "",
        dob: null,
        nationality: ["United Kingdom"],
        mobileCountryCode: "+44",
        mobile: "",
        gender: "",
        image: "",
        emailVerification: false,
        mobileVerification: false,
        passwordResetRequired: false,
        theme: "",
        language: "en",
        password: "",
        passwordOption: "setOwn",
      };

  const handleSubmit = async (values: any) => {
    if (passwordOption === "setOwn" && !values.password) {
      alert("Password is required when setting custom password");
      return;
    }

    const submittedData: UserSalesSubmitData = {
      user_first_name: values.firstName,
      user_last_name: values.lastName,
      user_email: values.email,
      user_dob: values.dob,
      nationality: values.nationality,
      user_mobile_country_code: values.mobileCountryCode,
      user_mobile: values.mobile,
      user_password: password || values.password,
      user_gender: values.gender,
      user_image: values.image,
      user_email_verification: values.emailVerification,
      user_mobile_verification: values.mobileVerification,
      user_password_reset_required: values.passwordResetRequired,
      user_theme: values.theme,
      user_language: values.language,
    };

    setUserData([...userData, submittedData]);
    navigate(role ? `${process.env.PUBLIC_URL}/users/${role.toLowerCase()}` : `${process.env.PUBLIC_URL}/users`);
  };

  const handleClickCancel = () => {
    navigate(role ? `${process.env.PUBLIC_URL}/users/${role.toLowerCase()}` : `${process.env.PUBLIC_URL}/users`);
  };

  const handlePasswordOptionChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    setFieldValue: any
  ) => {
    const value = e.target.value;
    setPasswordOption(value);
    setFieldValue("passwordOption", value);
    if (value === "autoGenerate") {
      const newPassword = generatePassword();
      setPassword(newPassword);
      setFieldValue("password", newPassword);
    } else if (value === "setOwn") {
      setPassword("");
      setFieldValue("password", "");
    } else if (value === "invitationMail") {
      setPassword("");
      setFieldValue("password", "");
    }
  };

  const nationalityOptions = organizationData.countries.map((country) => ({
    name: country.label,
    value: country.value,
    flag: country.flag,
  }));

  const genderOptions = [
    { name: t("Male"), value: "Male", flag: "" },
    { name: t("Female"), value: "Female", flag: "" },
    { name: t("Other"), value: "Other", flag: "" },
  ];

  const languageOptions = [
    { name: "English", value: "en", flag: "gb" },
    { name: "French", value: "fr", flag: "fr" },
  ];

  const countryCodeOptions = [
    { name: "+1", value: "+1", flag: "us" },
    { name: "+91", value: "+91", flag: "in" },
    { name: "+44", value: "+44", flag: "gb" },
  ];

  const handleCopyPassword = () => {
    if (passwordInputRef.current) {
      navigator.clipboard.writeText(passwordInputRef.current.value);
      setCopyTooltip(t("Copied"));
      setTimeout(() => setCopyTooltip(""), 2000);
    }
  };




  return (
    <>
      <Breadcrumbs
        mainTitle={isEditMode
          ? t(`Edit ${role ? getRoleTranslation(role) : t("User")} User`)
          : t(`Add ${role ? getRoleTranslation(role) : t("User")} User`)}
        parents={breadcrumbLinks}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ setFieldValue, values }) => (
                    <Form className="theme-form">
                      <Row className="g-3">
                        <Col sm="6">
                          <FormGroup>
                            <Label for="firstName" check>
                              {t("FirstName")} <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              id="firstName"
                              name="firstName"
                              type="text"
                              className="form-control"
                              placeholder={t("EnterFirstName")}
                            />
                            <ErrorMessage
                              name="firstName"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="6">
                          <FormGroup>
                            <Label for="lastName" check>
                              {t("LastName")}
                            </Label>
                            <Field
                              id="lastName"
                              name="lastName"
                              type="text"
                              className="form-control"
                              placeholder={t("EnterLastName")}
                            />
                            <ErrorMessage
                              name="lastName"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col sm="6">
                          <FormGroup>
                            <Label for="email" check>
                              {t("Email")} <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              id="email"
                              name="email"
                              type="email"
                              className="form-control"
                              placeholder={t("EnterEmail")}
                            />
                            <ErrorMessage
                              name="email"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="6">
                          <FormGroup>
                            <Label for="mobileCountryCode" check>
                              {t("MobileNumber")} <span className="txt-danger">*</span>
                            </Label>
                            <Row className="g-2">
                              <Col sm="4">
                                <Typeahead
                                  id="country-code-typeahead"
                                  labelKey="name"
                                  options={countryCodeOptions as FlagOption[]}
                                  placeholder={t("Select Code")}
                                  selected={countryCodeOptions.filter(
                                    (opt) => opt.value === values.mobileCountryCode
                                  ) as FlagOption[]}
                                  onChange={(selected) => {
                                    const sel = selected[0];
                                    if (sel && typeof sel === "object" && "value" in sel) {
                                      setFieldValue("mobileCountryCode", sel.value);
                                    } else {
                                      setFieldValue("mobileCountryCode", "");
                                    }
                                  }}
                                  renderMenuItemChildren={(option) => {
                                    const opt = option as FlagOption;
                                    return (
                                      <div className="d-flex align-items-center">
                                        {opt.flag && (
                                          <img
                                            src={`/assets/fonts/flag-icon/${opt.flag}.svg`}
                                            alt={opt.name}
                                            style={{ width: 20, height: 15, marginRight: 8 }}
                                          />
                                        )}
                                        <span>{opt.name}</span>
                                      </div>
                                    );
                                  }}
                                />
                                <ErrorMessage
                                  name="mobileCountryCode"
                                  component="span"
                                  className="text-danger"
                                />
                              </Col>
                              <Col sm="8">
                                <Field
                                  id="mobile"
                                  name="mobile"
                                  type="text"
                                  className="form-control"
                                  placeholder={t("EnterMobileNumber")}
                                />
                                <ErrorMessage
                                  name="mobile"
                                  component="span"
                                  className="text-danger"
                                />
                              </Col>
                            </Row>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col sm="6">
                          <FormGroup>
                            <Label for="passwordOption" check>
                              {t("Password Option")} <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              id="passwordOption"
                              as="select"
                              name="passwordOption"
                              value={passwordOption}
                              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                handlePasswordOptionChange(e, setFieldValue)
                              }
                              className="form-control"
                            >
                              <option value="setOwn">{t("SetCustomPassword")}</option>
                              <option value="autoGenerate">{t("AutoGenerate")}</option>
                              <option value="invitationMail">{t("InvitationMail")}</option>
                            </Field>
                            <ErrorMessage
                              name="passwordOption"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="6">
                          {passwordOption === "setOwn" && (
                            <FormGroup>
                              <Label for="password" className="mb-1">
                                {t("EnterCustomPassword")}
                              </Label>
                              <Field
                                id="password"
                                name="password"
                                type="password"
                                className="form-control"
                                placeholder={t("EnterPassword")}
                                innerRef={passwordInputRef}
                              />
                              <ErrorMessage
                                name="password"
                                component="span"
                                className="text-danger"
                              />
                            </FormGroup>
                          )}
                          {passwordOption === "autoGenerate" && (
                            <FormGroup>
                              <Label className="mb-1">{t("AutoGeneratedPassword")}</Label>
                              <div style={{ position: "relative" }}>
                                <Input
                                  type="password"
                                  className="form-control pe-5"
                                  value={password}
                                  readOnly
                                  innerRef={passwordInputRef}
                                />
                                <button
                                  type="button"
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    right: 10,
                                    transform: "translateY(-50%)",
                                    border: "none",
                                    background: "none",
                                    padding: 0,
                                    cursor: "pointer",
                                  }}
                                  onClick={handleCopyPassword}
                                  tabIndex={-1}
                                  aria-label="Copy password"
                                >
                                  <i className="fa fa-copy"></i>
                                  {copyTooltip && (
                                    <span
                                      style={{
                                        position: "absolute",
                                        top: "-30px",
                                        right: 0,
                                        backgroundColor: "#333",
                                        color: "#fff",
                                        padding: "2px 6px",
                                        borderRadius: "4px",
                                        fontSize: "12px",
                                        zIndex: 10,
                                      }}
                                    >
                                      {copyTooltip}
                                    </span>
                                  )}
                                </button>
                              </div>
                            </FormGroup>
                          )}
                          {passwordOption === "invitationMail" && (
                            <FormGroup>
                              <Label className="mb-1">{t("InvitationLink")}</Label>
                              <Input
                                type="text"
                                className="form-control"
                                value="Invitation link will be sent to email"
                                readOnly
                                disabled
                              />
                            </FormGroup>
                          )}
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col sm="3">
                          <FormGroup>
                            <Label for="gender" check>
                              {t("Gender")}
                            </Label>
                            <Field
                              id="gender"
                              as="select"
                              name="gender"
                              className="form-control"
                            >
                              <option value="">{t("SelectGender")}</option>
                              {genderOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.name}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name="gender"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="3">
                          <FormGroup className="d-flex flex-column align-items-stretch">
                            <Label for="dob" check>
                              {t("DateOfBirth")}
                            </Label>
                            <ReactDatePicker
                              id="dob"
                              className="datepicker-here form-control"
                              selected={values.dob}
                              onChange={(date: Date | null) => setFieldValue("dob", date)}
                              placeholderText={t("SelectDOB")}
                            />
                            <ErrorMessage
                              name="dob"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="3">
                          <FormGroup>
                            <Label for="nationality" check>
                              {t("Nationality")}
                            </Label>
                            <Typeahead
                              id="nationality-typeahead"
                              labelKey="name"
                              options={nationalityOptions as FlagOption[]}
                              placeholder={t("SelectNationality")}
                              selected={nationalityOptions.filter((opt) =>
                                values.nationality.includes(opt.value)
                              ) as FlagOption[]}
                              onChange={(selected) => {
                                const arr = selected.filter(
                                  (sel) => typeof sel === "object" && "value" in sel
                                ) as FlagOption[];
                                setFieldValue("nationality", arr.map((opt) => opt.value));
                              }}
                              renderMenuItemChildren={(option) => {
                                const opt = option as FlagOption;
                                return (
                                  <div className="d-flex align-items-center">
                                    {opt.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${opt.flag}.svg`}
                                        alt={opt.name}
                                        style={{ width: 20, height: 15, marginRight: 8 }}
                                      />
                                    )}
                                    <span>{opt.name}</span>
                                  </div>
                                );
                              }}
                              multiple={false}
                            />
                            <ErrorMessage
                              name="nationality"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col sm="3">
                          <FormGroup>
                            <Label for="language" check>
                              {t("Language")}
                            </Label>
                            <Typeahead
                              id="language-typeahead"
                              labelKey="name"
                              options={languageOptions as FlagOption[]}
                              placeholder={t("SelectLanguage")}
                              selected={languageOptions.filter(
                                (opt) => opt.value === values.language
                              ) as FlagOption[]}
                              onChange={(selected) => {
                                const sel = selected[0];
                                if (sel && typeof sel === "object" && "value" in sel) {
                                  setFieldValue("language", sel.value);
                                } else {
                                  setFieldValue("language", "");
                                }
                              }}
                              renderMenuItemChildren={(option) => {
                                const opt = option as FlagOption;
                                return (
                                  <div className="d-flex align-items-center">
                                    {opt.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${opt.flag}.svg`}
                                        alt={opt.name}
                                        style={{ width: 20, height: 15, marginRight: 8 }}
                                      />
                                    )}
                                    <span>{opt.name}</span>
                                  </div>
                                );
                              }}
                              multiple={false}
                            />
                            <ErrorMessage
                              name="language"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col sm="12">
                          <FormGroup>
                            <Label for="image" check>
                              {t("Image")}
                            </Label>
                            <FilePond
                              files={files}
                              allowReorder={true}
                              allowMultiple={false}
                              maxFiles={1}
                              credits={false}
                              onupdatefiles={(fileItems) => {
                                setFiles(fileItems.map((fileItem) => fileItem.file));
                                setFieldValue(
                                  "image",
                                  fileItems[0]?.file?.name || ""
                                );
                              }}
                              labelIdle={t("DragDropOrBrowse")}
                            />
                            <ErrorMessage
                              name="image"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col className="text-end">
                          <Button
                            onClick={handleClickCancel}
                            color="secondary"
                            className="me-2"
                          >
                            {t("Cancel")}
                          </Button>
                          <Button color="primary" type="submit">
                            {saveButtonText}
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddUserPage;